/**
 * NOS-47: Public events listing at /events or /:orgSlug/events.
 * Fetches published events, shows upcoming and past.
 * NOS-265: org/nav/customizations shell via usePublicSiteShell + PublicSitePage.
 */

import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { supabase } from "@/lib/supabase"
import { PublicSitePage } from "@/components/PublicSitePage"
import { SEOHead } from "@/components/public/SEOHead"
import { getPublicSiteOrigin } from "@/lib/publicSiteUrl"
import { usePublicSiteShell } from "@/hooks/usePublicSiteShell"
import type { Organization, OrgCustomizations } from "@/templates/types"
import { Card, CardContent } from "@/components/ui/card"
import { Calendar, MapPin } from "lucide-react"

interface EventRow {
  id: string
  org_id: string
  title: string
  slug: string
  start_date: string
  end_date: string | null
  location_name: string | null
  location_type: "in_person" | "virtual" | "hybrid" | null
  cover_image: string | null
}

interface EventsListingPageProps {
  orgSlug: string
  org?: Organization | null
  customizations?: OrgCustomizations | null
  basePath?: string
}

function formatEventDate(start: string, end: string | null): string {
  const startDate = new Date(start)
  const opts: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }
  if (end) {
    const endDate = new Date(end)
    return `${startDate.toLocaleDateString("en-US", opts)} – ${endDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`
  }
  return startDate.toLocaleDateString("en-US", opts)
}

export function EventsListingPage({
  orgSlug,
  org: orgProp,
  customizations: custProp,
  basePath = "",
}: EventsListingPageProps) {
  const shell = usePublicSiteShell(orgSlug, {
    org: orgProp,
    customizations: custProp,
    basePath,
  })
  const { org, orgName, primaryColor } = shell

  const [upcoming, setUpcoming] = useState<EventRow[]>([])
  const [past, setPast] = useState<EventRow[]>([])
  const [loading, setLoading] = useState(true)

  const pathForEvent = (slug: string) => `${basePath}/events/${slug}`

  const orgId = org?.id
  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!orgId) return

      const now = new Date().toISOString()

      const { data: upcomingData } = await supabase
        .from("events")
        .select("id, org_id, title, slug, start_date, end_date, location_name, location_type, cover_image")
        .eq("org_id", orgId)
        .eq("is_published", true)
        .gte("start_date", now)
        .order("start_date", { ascending: true })
        .limit(50)

      const { data: pastData } = await supabase
        .from("events")
        .select("id, org_id, title, slug, start_date, end_date, location_name, location_type, cover_image")
        .eq("org_id", orgId)
        .eq("is_published", true)
        .lt("start_date", now)
        .order("start_date", { ascending: false })
        .limit(20)

      if (cancelled) return
      setUpcoming((upcomingData ?? []) as EventRow[])
      setPast((pastData ?? []) as EventRow[])
      setLoading(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [orgId])

  const canonicalUrl = useMemo(() => {
    if (!org) return ""
    return `${getPublicSiteOrigin(org)}/events`
  }, [org])

  const description = `Upcoming and past events from ${orgName}`

  const EventCard = ({ event }: { event: EventRow }) => (
    <Link to={pathForEvent(event.slug)} className="group">
      <Card className="h-full overflow-hidden transition hover:shadow-lg">
        <div className="aspect-video overflow-hidden bg-muted">
          {event.cover_image ? (
            <img
              src={event.cover_image}
              alt=""
              className="h-full w-full object-cover transition group-hover:scale-105"
            />
          ) : (
            <div
              className="flex h-full items-center justify-center"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <Calendar className="h-12 w-12 opacity-50" style={{ color: primaryColor }} />
            </div>
          )}
        </div>
        <CardContent className="p-4">
          <h2 className="font-semibold group-hover:underline">{event.title}</h2>
          <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4 shrink-0" />
            {formatEventDate(event.start_date, event.end_date)}
          </p>
          {event.location_name && (
            <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4 shrink-0" />
              {event.location_name}
            </p>
          )}
        </CardContent>
      </Card>
    </Link>
  )

  return (
    <PublicSitePage
      shell={shell}
      head={
        org && canonicalUrl ? (
          <SEOHead
            title={`Events | ${orgName}`}
            description={description}
            canonicalUrl={canonicalUrl}
            ogType="website"
            imageUrl={org.logo_url}
          />
        ) : null
      }
    >
      <main className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
        <header className="mb-10">
          <h1 className="text-3xl font-bold" style={{ color: primaryColor }}>
            Events
          </h1>
          <p className="mt-1 text-muted-foreground">
            Upcoming and past events from {orgName}
          </p>
        </header>

        {loading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-0">
                  <div className="aspect-video bg-muted" />
                  <div className="space-y-2 p-4">
                    <div className="h-4 w-3/4 rounded bg-muted" />
                    <div className="h-3 w-full rounded bg-muted" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="space-y-12">
            {upcoming.length > 0 && (
              <section>
                <h2 className="mb-6 text-xl font-semibold">Upcoming events</h2>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {upcoming.map((event) => (
                    <EventCard key={event.id} event={event} />
                  ))}
                </div>
              </section>
            )}

            {past.length > 0 && (
              <section>
                <h2 className="mb-6 text-xl font-semibold text-muted-foreground">Past events</h2>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {past.map((event) => (
                    <EventCard key={event.id} event={event} />
                  ))}
                </div>
              </section>
            )}

            {!loading && upcoming.length === 0 && past.length === 0 && (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
                <Calendar className="h-12 w-12 text-muted-foreground" />
                <p className="mt-4 text-lg font-medium">No events yet</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Check back later for upcoming events.
                </p>
              </div>
            )}
          </div>
        )}
      </main>
    </PublicSitePage>
  )
}
